import { useCallback, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useTheme } from '@mui/material';

import { gql } from '@graphql/__generated__';
import {
  type GetPropertyTypesQuery,
  type ListingsMatchingFiltersPropertySearchesFragment,
  type Lots_Bool_Exp,
  Lots_Status_Enum_Enum,
} from '@graphql/__generated__/graphql';
import { INSERT_BUYER_LEADS_MANY } from '@modules/_shared/components/buyer-lead/buyerLeadQueries';
import { buildOriginationActivity } from '@modules/_shared/components/buyer-lead/buyerLeadUtils';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { addWhereClause } from '@modules/_shared/components/filters/Filters';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getStartOfDayOneYearAgo } from '@modules/_shared/utils/dates';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { isPropertyTypeInCompositeGroup } from '@modules/_shared/utils/propertyTypeHelpers';
import { UserBuyerLeadMatchingList } from '@modules/users/components/UserBuyerLeadMatchingList';
import { GET_USER_BUYERS_LEAD } from '@modules/users/components/UserBuyerLeadsKanban';
import { useLocale } from '@shared/hooks/locale';

type UserBuyerLeadMatchingDrawerProps = {
  onClose: () => void;
  userId: string;
  excludedListingIds: string[];
  destinationStageId?: string;
};

export const LISTINGS_MATCHING_FILTERS_PROPERTY_SEARCHES_FRAGMENT =
  gql(/* GraphQL */ `
    fragment ListingsMatchingFiltersPropertySearches on property_searches {
      id
      offer_type
      property_types {
        property_type {
          id
          name
          type
          composite_type_object {
            id
            label
            name
          }
        }
      }
      property_searches_places {
        place_id
      }
      sale_price_lte
      sale_price_gte
      monthly_rent_lte
      monthly_rent_gte
    }
  `);

type CombinedPropertySearch = Partial<
  Omit<ListingsMatchingFiltersPropertySearchesFragment, 'id'>
>;

const combinePropertySearches = (
  propertySearches: ListingsMatchingFiltersPropertySearchesFragment[],
) => {
  const combinedPropertySearch: CombinedPropertySearch = {};

  for (const propertySearch of propertySearches) {
    if (propertySearch.offer_type != null) {
      if (typeof combinedPropertySearch.offer_type === 'undefined') {
        // undefined means we haven't set an offer type yet, so that we don't set if we previsouly reset to null.
        combinedPropertySearch.offer_type = propertySearch.offer_type;
      } else if (
        combinedPropertySearch.offer_type !== propertySearch.offer_type
      ) {
        // If offer type is different, reset offer type
        combinedPropertySearch.offer_type = null;
      }
    }

    if (propertySearch.monthly_rent_gte != null) {
      // Take the lowest value because it's less restrictive.
      combinedPropertySearch.monthly_rent_gte = Math.min(
        combinedPropertySearch.monthly_rent_gte ?? Number.MAX_SAFE_INTEGER,
        propertySearch.monthly_rent_gte,
      );
    }

    if (propertySearch.monthly_rent_lte != null) {
      // Take the highest value because it's less restrictive.
      combinedPropertySearch.monthly_rent_lte = Math.max(
        combinedPropertySearch.monthly_rent_lte ?? Number.MIN_SAFE_INTEGER,
        propertySearch.monthly_rent_lte,
      );
    }

    if (propertySearch.sale_price_gte != null) {
      combinedPropertySearch.sale_price_gte = Math.min(
        combinedPropertySearch.sale_price_gte ?? Number.MAX_SAFE_INTEGER,
        propertySearch.sale_price_gte,
      );
    }

    if (propertySearch.sale_price_lte != null) {
      combinedPropertySearch.sale_price_lte = Math.max(
        combinedPropertySearch.sale_price_lte ?? Number.MIN_SAFE_INTEGER,
        propertySearch.sale_price_lte,
      );
    }

    if (propertySearch.property_types != null) {
      combinedPropertySearch.property_types = [
        ...(combinedPropertySearch.property_types ?? []),
        ...propertySearch.property_types.filter(
          pt =>
            !combinedPropertySearch.property_types?.some(
              pt2 => pt2.property_type.id === pt.property_type.id,
            ),
        ),
      ];
    }

    if (propertySearch.property_searches_places != null) {
      combinedPropertySearch.property_searches_places = [
        ...(combinedPropertySearch.property_searches_places ?? []),
        ...propertySearch.property_searches_places.filter(
          psp =>
            !combinedPropertySearch.property_searches_places?.some(
              psp2 => psp2.place_id === psp.place_id,
            ),
        ),
      ];
    }
  }

  return combinedPropertySearch;
};

export const getDefaultListingsMatchingFilters = (
  propertySearches: ListingsMatchingFiltersPropertySearchesFragment[],
  propertyTypes: GetPropertyTypesQuery['property_types'],
  existingParams?: URLSearchParams,
) => {
  const searchParams = new URLSearchParams(existingParams);

  // Default to max
  searchParams.set('matching_limit', '500');

  let where: Lots_Bool_Exp = {
    created_at: { _gte: getStartOfDayOneYearAgo().toISOString() },
    status: {
      _in: [
        Lots_Status_Enum_Enum.Available,
        Lots_Status_Enum_Enum.AvailableOffMarket,
      ],
    },
  };

  if (propertySearches.length > 0) {
    const combinedPropertySearch = combinePropertySearches(propertySearches);

    if (combinedPropertySearch.offer_type != null) {
      where = addWhereClause(
        where,
        ['lots_bool_exp', 'offer_type', '_in'],
        [combinedPropertySearch.offer_type === 'buy' ? 'sell' : 'rent'],
      );

      if (combinedPropertySearch.sale_price_gte != null) {
        where = addWhereClause(
          where,
          ['lots_bool_exp', 'sale_price', '_gte'],
          combinedPropertySearch.sale_price_gte,
        );
      }

      if (combinedPropertySearch.sale_price_lte != null) {
        where = addWhereClause(
          where,
          ['lots_bool_exp', 'sale_price', '_lte'],
          combinedPropertySearch.sale_price_lte,
        );
      }

      if (combinedPropertySearch.monthly_rent_gte != null) {
        where = addWhereClause(
          where,
          ['lots_bool_exp', 'rent_net', '_gte'],
          combinedPropertySearch.monthly_rent_gte,
        );
      }

      if (combinedPropertySearch.monthly_rent_lte != null) {
        where = addWhereClause(
          where,
          ['lots_bool_exp', 'rent_net', '_lte'],
          combinedPropertySearch.monthly_rent_lte,
        );
      }
    }

    if (
      combinedPropertySearch.property_searches_places != null &&
      combinedPropertySearch.property_searches_places.length > 0
    ) {
      const searchPlaces = combinedPropertySearch.property_searches_places.map(
        searchPlace => searchPlace.place_id,
      );

      where = addWhereClause(where, ['lots_bool_exp', 'property', 'places'], {
        _or: [
          { id: { _in: searchPlaces } },
          { parent_places: { id: { _in: searchPlaces } } },
        ],
      });
    }

    if (
      combinedPropertySearch.property_types != null &&
      combinedPropertySearch.property_types.length > 0
    ) {
      const selectedPropertyTypeNames =
        combinedPropertySearch.property_types.flatMap(({ property_type }) =>
          property_type.type === 'composite'
            ? propertyTypes
                .filter(pt =>
                  isPropertyTypeInCompositeGroup(pt, property_type.name),
                )
                .map(pt => pt.name)
            : [property_type.name],
        );

      const dedupPropertyTypeNames = [...new Set(selectedPropertyTypeNames)];

      where = addWhereClause(
        where,
        ['lots_bool_exp', 'property', 'property_type', 'name', '_in'],
        dedupPropertyTypeNames,
      );
    }

    // TODO: Maybe add surface, rooms and features filters.
  }

  searchParams.set('matching_where', JSON.stringify(where));

  return searchParams;
};

export const UserBuyerLeadMatchingDrawer: React.FC<
  UserBuyerLeadMatchingDrawerProps
> = ({ onClose, userId, excludedListingIds, destinationStageId }) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const { breakpoints } = useTheme();

  const [mutationError, setMutationError] = useState<ErrorLike | null>(null);

  const [insertBuyerLeads, { loading }] = useMutation(INSERT_BUYER_LEADS_MANY);

  const handleAddBuyers = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) {
        return;
      }

      setMutationError(null);
      insertBuyerLeads({
        variables: {
          buyer_leads: ids.map(id => ({
            user_id: userId,
            lot_id: id,
            source: 'matching',
            stage_id: destinationStageId,
            activities: buildOriginationActivity(
              userId,
              me?.is_admin ? me?.id : undefined,
            ),
          })),
        },
        onCompleted: data => {
          if (data.insert_buyer_leads != null) {
            onClose();
            emitFeedRefresh();
          }
        },
        onError: setMutationError,
        refetchQueries: [GET_USER_BUYERS_LEAD],
      });
    },
    [
      insertBuyerLeads,
      userId,
      destinationStageId,
      me?.is_admin,
      me?.id,
      onClose,
    ],
  );

  return (
    <Drawer
      onClose={onClose}
      title={t('Listings matching')}
      maxWidth={breakpoints.values.lg}
    >
      <UserBuyerLeadMatchingList
        excludedListings={excludedListingIds}
        onAdd={handleAddBuyers}
        isAdding={loading}
        onCancel={onClose}
      />
      <MutationErrorSnackbar
        error={mutationError}
        onClose={() => setMutationError(null)}
      />
    </Drawer>
  );
};
